<template>
    <div
        v-if="tool.name !== 'get_capabilities' && hasContent"
        class="flex flex-col gap-3 items-start"
        :class="tool.name === ACTION.QUERY_RECENT_DOCUMENT_UPLOADS && tool.completed ? 'mt-2' : 'mt-4'"
        :aria-live="tool.name === ACTION.QUERY_RECENT_DOCUMENT_UPLOADS ? 'polite' : undefined">
        <ChatAgentToolCollaboration v-if="tool.name === ACTION.EXPLORE_COLLABORATION" v-bind="tool" :running="isRunning" />
        <template v-else>
            <NadiaThinkingShapes v-if="usesInlineLoader && !tool.completed" :status-text="label" class="px-2" />
            <Transition name="fade">
                <component :is="toolComponent" v-if="tool.completed" v-bind="tool" :hide-email-button="hideEmailButton" />
            </Transition>
        </template>
    </div>
</template>

<script lang="ts">
import { ACTION, ARTIFACT_TOOL_ACTIONS, isArtifactToolName } from "~vue/chatActions";
import type { ChatMessageLine } from "~vue/types/chat";

const INLINE_LOADER_TOOLS: string[] = [ACTION.ACTION_ITEMS, ACTION.FOLLOW_UP, ACTION.EMAIL_CHAT_SUMMARY, ...ARTIFACT_TOOL_ACTIONS];
const RESULT_CARD_TOOLS: string[] = [
    ACTION.FOLLOW_UP,
    ACTION.EMAIL_CHAT_SUMMARY,
    ACTION.ACTION_ITEMS,
    ACTION.DOCUMENT_CREATOR,
    ACTION.RECOMMEND_LEARNING_CONTENT,
    ACTION.PM_PICKER,
    ACTION.GET_ASSESSMENT_RESULTS,
    ACTION.QUERY_RECENT_DOCUMENT_UPLOADS,
    // read_artifact feeds the model only; its card renders nothing, so a completed read must not claim a row.
    ...ARTIFACT_TOOL_ACTIONS.filter((action) => action !== ACTION.READ_ARTIFACT),
];

// Whether a tool call paints anything right now. ChatMessageList uses it to skip the row
// entirely, since an empty row still emits a labeled assistant article and a gap in the chat.
// Document cards render nothing once nadia_artifacts is off, so their historical calls hide too.
export const toolCallHasContent = (tool: { name: string; completed: boolean }, { artifactsEnabled = true } = {}): boolean => {
    if (tool.name === ACTION.EXPLORE_COLLABORATION) {
        return true;
    }
    if (!artifactsEnabled && isArtifactToolName(tool.name)) {
        return false;
    }
    return tool.completed ? RESULT_CARD_TOOLS.includes(tool.name) : INLINE_LOADER_TOOLS.includes(tool.name);
};

export interface ChatToolCall {
    name: string;
    completed: boolean;
    action?: ChatMessageLine | null;
    messageId?: number | null;
    lineIdx?: number | null;
    statusText?: string | null;
}
</script>

<script setup lang="ts">
import ChatAgentToolArtifact from "~vue/ChatDetail/artifacts/ChatAgentToolArtifact.vue";
import ChatAgentToolActionItems from "~vue/ChatDetail/ChatAgentToolActionItems.vue";
import ChatAgentToolAssessmentResults from "~vue/ChatDetail/ChatAgentToolAssessmentResults.vue";
import ChatAgentToolCollaboration from "~vue/ChatDetail/ChatAgentToolCollaboration.vue";
import ChatAgentToolDocumentCreator from "~vue/ChatDetail/ChatAgentToolDocumentCreator.vue";
import ChatAgentToolEmailChatSummary from "~vue/ChatDetail/ChatAgentToolEmailChatSummary.vue";
import ChatAgentToolFollowup from "~vue/ChatDetail/ChatAgentToolFollowup.vue";
import ChatAgentToolLearningResource from "~vue/ChatDetail/ChatAgentToolLearningResource.vue";
import ChatAgentToolRecentDocuments from "~vue/ChatDetail/ChatAgentToolRecentDocuments.vue";
import ChatPerformancePicker from "~vue/ChatDetail/ChatPerformancePicker.vue";
import NadiaThinkingShapes from "~vue/NadiaThinkingShapes.vue";
import { computed } from "vue";

const props = withDefaults(defineProps<{ tool?: ChatToolCall; hideEmailButton?: boolean; isRunning?: boolean }>(), {
    tool: () => ({ name: "", completed: false }),
    hideEmailButton: false,
    isRunning: true,
});

// Only these tools surface progress: the shapes loader carries the generating copy, and the card
// speaks for itself once done — every other tool shows nothing while running.
const usesInlineLoader = computed(() => INLINE_LOADER_TOOLS.includes(props.tool.name));

const toolComponent = computed(() => {
    switch (props.tool.name) {
        case ACTION.FOLLOW_UP:
            return ChatAgentToolFollowup;
        case ACTION.EMAIL_CHAT_SUMMARY:
            return ChatAgentToolEmailChatSummary;
        case ACTION.ACTION_ITEMS:
            return ChatAgentToolActionItems;
        case ACTION.DOCUMENT_CREATOR:
            return ChatAgentToolDocumentCreator;
        case ACTION.RECOMMEND_LEARNING_CONTENT:
            return ChatAgentToolLearningResource;
        case ACTION.PM_PICKER:
            return ChatPerformancePicker;
        case ACTION.GET_ASSESSMENT_RESULTS:
            return ChatAgentToolAssessmentResults;
        case ACTION.QUERY_RECENT_DOCUMENT_UPLOADS:
            return ChatAgentToolRecentDocuments;
        case ACTION.CREATE_ARTIFACT:
        case ACTION.EDIT_ARTIFACT:
        case ACTION.REWRITE_ARTIFACT:
        case ACTION.READ_ARTIFACT:
        case ACTION.LIST_ARTIFACTS:
            return ChatAgentToolArtifact;
    }

    return null;
});

const hasContent = computed(() => toolCallHasContent(props.tool));

// WorkIQ tools are deliberately absent from both label maps: they render through the inline
// progress line in ChatMessageList (useWorkIqToolProgress), never through this pill.
const label = computed(() => {
    if (props.tool.completed) {
        switch (props.tool.name) {
            case ACTION.FOLLOW_UP:
                return "Invite generated";
            case ACTION.EMAIL_CHAT_SUMMARY:
                return "Summary generated";
            case ACTION.ACTION_ITEMS:
                return "Action item generated";
            case ACTION.DOCUMENT_CREATOR:
                return "Document generated";
            case ACTION.RECOMMEND_LEARNING_CONTENT:
                return "Learning resource found";
            case "list_meeting_transcripts":
                return "Meetings fetched";
            case "get_meeting_outlines":
                return "Meeting details reviewed";
            case "get_meeting_transcript":
                return "Meeting transcript reviewed";
            default:
                return "Completed";
        }
    }

    // Live progress text (e.g. Work IQ's own "Checking your calendar..." lifecycle
    // messages) takes priority over the static per-tool label while running.
    if (props.tool.statusText) {
        return props.tool.statusText;
    }

    switch (props.tool.name) {
        case ACTION.FOLLOW_UP:
            return "Invite generating";

        case ACTION.EMAIL_CHAT_SUMMARY:
            return "Generating a summary";

        case ACTION.ACTION_ITEMS:
            return "Generating your action item";

        case ACTION.DOCUMENT_CREATOR:
            return "Generating your document";

        case ACTION.RECOMMEND_LEARNING_CONTENT:
            return "Finding a learning resource...";

        case ACTION.CREATE_ARTIFACT:
            return "Saving your document...";

        case ACTION.EDIT_ARTIFACT:
        case ACTION.REWRITE_ARTIFACT:
            return "Updating your document...";

        case ACTION.READ_ARTIFACT:
            return "Reading your document...";

        case ACTION.LIST_ARTIFACTS:
            return "Looking up your documents...";

        case "list_meeting_transcripts":
            return "Fetching meetings...";

        case "get_meeting_outlines":
            return "Reading meeting details...";

        case "get_meeting_transcript":
            return "Reading meeting transcript...";

        case ACTION.PM_PICKER:
            return "Looking up your performance tasks...";

        case ACTION.GET_ASSESSMENT_RESULTS:
            return "Fetching Reflections results...";

        case ACTION.QUERY_RECENT_DOCUMENT_UPLOADS:
            return "Searching your recent uploads...";

        default:
            return "Running...";
    }
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 300ms ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

i.bi::before {
    font-weight: bold !important;
}
</style>
